import { useFriends } from "../context/FriendsContext";
import React, { useEffect, useRef, useState } from "react";
import { useComments } from "../context/CommentsContext";
import { useUser } from "../context/UserContext";
import useDraft from "../hooks/useDraft";
import styled, { useTheme } from "styled-components";
const DiscussionRoot = styled.div`
  color: ${({ theme }) => theme.colors.text};
  min-width: 0;
  font-size: 14px;
  h3 {
    font-size: 18px;
    margin-bottom: 16px;
  }
  p {
    margin: 8px 0;
    line-height: 1.5;
  }
  small {
    color: ${({ theme }) => theme.colors.textSecondary};
  }
  form {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
  }
  input {
    flex: 1;
    min-width: 100px;
    border: 2px solid
      ${({ theme }) => (theme.isDarkMode ? "#2a2d30" : "#e4e6eb")};
    border-radius: 18px;
    padding: 11px 14px;
    color: inherit;
    background: ${({ theme }) => (theme.isDarkMode ? "#232629" : "#f0f2f5")};
    font: inherit;
  }
  button {
    border: none;
    border-radius: 16px;
    padding: 7px 11px;
    color: inherit;
    background: ${({ theme }) => (theme.isDarkMode ? "#ffffff0c" : "#00000008")};
    font: inherit;
    font-size: 12px;
    cursor: pointer;
  }
  form button:not([type="button"]) {
    background: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.onPrimary};
    font-weight: 600;
  }
  button:disabled {
    opacity: 0.5;
    cursor: default;
  }
  input:focus-visible,
  button:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
  [aria-pressed="true"] {
    color: ${({ theme }) => theme.colors.primary};
  }
`;
const buttonStyle = { cursor: "pointer" };
function CommentNode({ comment, category, id, depth = 0 }) {
  const { friends } = useFriends();
  const { user } = useUser(),
    theme = useTheme();
  const { addComment, deleteComment, updateComment, likeComment, loadReplies } =
    useComments();
  const [reply, setReply] = useState(false),
    [editing, setEditing] = useState(false),
    [edit, setEdit] = useState("");
  const [text, setText, saved] = useDraft(
    `reply:${category}:${id}:${comment.id}`,
  );
  const [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const lock = useRef(false);
  const run = async (fn) => {
    if (lock.current) return;
    lock.current = true;
    setBusy(true);
    setError("");
    try {
      await fn();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
      lock.current = false;
    }
  };
  return (
    <article
      style={{
        marginTop: 12,
        padding: 12,
        background: theme.isDarkMode ? "#232629" : "#f8f9fa",
        borderRadius: 12,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {!comment.deleted_at && (
          <img
            src={comment.user_avatar || "/assets/avatar-placeholder.png"}
            alt=""
            width="28"
            height="28"
            style={{ borderRadius: "50%" }}
          />
        )}
        <strong>
          {comment.deleted_at ? "Комментарий удалён" : comment.user_name}
        </strong>
        {!comment.deleted_at &&
          friends.some((friend) => friend.id === comment.user_id) && (
            <small style={{ color: theme.colors.primary }}>Друг</small>
          )}
        <small style={{ marginLeft: "auto" }}>
          {new Date(comment.created_at).toLocaleDateString()}
        </small>
      </div>
      {!comment.deleted_at &&
        (editing ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void run(async () => {
                await updateComment(comment.id, edit, category, id);
                setEditing(false);
              });
            }}
          >
            <input
              aria-label="Изменить комментарий"
              value={edit}
              maxLength={150}
              onChange={(e) => setEdit(e.target.value)}
            />
            <button disabled={busy || !edit.trim()}>Сохранить</button>
            <button type="button" onClick={() => setEditing(false)}>
              Отмена
            </button>
          </form>
        ) : (
          <p style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
            {comment.text}
          </p>
        ))}
      {user && (
        <div
          style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}
        >
          <button style={buttonStyle} onClick={() => setReply(!reply)}>
            Ответить
          </button>
          {!comment.deleted_at && (
            <button
              style={buttonStyle}
              aria-pressed={(comment.likes || []).includes(user.id)}
              disabled={busy}
              onClick={() => void run(() => likeComment(comment, category, id))}
            >
              ♥ {(comment.likes || []).length}
            </button>
          )}
          {!comment.deleted_at && comment.user_id === user.id && (
            <>
              <button
                style={buttonStyle}
                onClick={() => {
                  setEditing(true);
                  setEdit(comment.text);
                }}
              >
                Изменить
              </button>
              <button
                style={buttonStyle}
                disabled={busy}
                onClick={() => {
                  if (window.confirm("Удалить комментарий? Ответы сохранятся."))
                    void run(() => deleteComment(comment.id, category, id));
                }}
              >
                Удалить
              </button>
            </>
          )}
        </div>
      )}
      {reply && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void run(async () => {
              const sent = text;
              await addComment(category, id, sent, comment.id);
              setText((value) => (value === sent ? "" : value));
            });
          }}
          style={{ marginTop: 8 }}
        >
          <input
            aria-label="Написать ответ"
            value={text}
            maxLength={150}
            onChange={(e) => setText(e.target.value)}
            style={{ maxWidth: "100%" }}
          />
          <button disabled={busy || !text.trim()}>Отправить</button>
          {saved && <small role="status"> Черновик сохранён</small>}
        </form>
      )}
      {error && <p role="alert">{error}</p>}
      <div style={{ marginLeft: depth < 3 ? 12 : 0 }}>
        {(comment.replies || []).map((child) => (
          <CommentNode
            key={child.id}
            comment={child}
            category={category}
            id={id}
            depth={depth + 1}
          />
        ))}
      </div>
      {((!comment.replies_loaded &&
        Number(comment.reply_count) > (comment.replies || []).length) ||
        comment.replies_cursor) && (
        <button
          style={{ ...buttonStyle, marginTop: 8 }}
          disabled={busy}
          onClick={() => void run(() => loadReplies(category, id, comment))}
        >
          Показать ответы
          {comment.replies_loaded ? " ещё" : ` (${comment.reply_count})`}
        </button>
      )}
    </article>
  );
}
export function CommentList({ category, id }) {
  const { pages, error, loadComments } = useComments(),
    page = pages[`${category}_${id}`];
  return (
    <DiscussionRoot>
      {error && (
        <p role="alert">
          {error}{" "}
          <button onClick={() => loadComments(category, id)}>Повторить</button>
        </p>
      )}
      {(page?.items || []).map((comment) => (
        <CommentNode
          key={comment.id}
          comment={comment}
          category={category}
          id={id}
        />
      ))}
      {page?.loading && <p role="status">Загрузка комментариев…</p>}
      {page?.next_cursor && (
        <button
          style={{ ...buttonStyle, marginTop: 12 }}
          disabled={page.loading}
          onClick={() => loadComments(category, id, page.next_cursor)}
        >
          Показать ещё комментарии
        </button>
      )}
    </DiscussionRoot>
  );
}
export default function CommentDiscussion({ category, id }) {
  const { loadComments, addComment } = useComments(),
    { user } = useUser();
  const [text, setText, saved] = useDraft(`review:${category}:${id}`),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const lock = useRef(false);
  useEffect(() => {
    void loadComments(category, id);
  }, [category, id, loadComments]);
  return (
    <DiscussionRoot as="section" style={{ marginTop: 24 }}>
      <h3>Комментарии</h3>
      {user && (
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            if (lock.current || !text.trim()) return;
            lock.current = true;
            setBusy(true);
            setError("");
            const sent = text;
            try {
              await addComment(category, id, sent);
              setText((value) => (value === sent ? "" : value));
            } catch (e) {
              setError(e.message);
            } finally {
              lock.current = false;
              setBusy(false);
            }
          }}
        >
          <input
            aria-label="Написать комментарий"
            value={text}
            maxLength={150}
            onChange={(e) => setText(e.target.value)}
            style={{ maxWidth: "100%", padding: 10 }}
          />
          <button disabled={busy || !text.trim()}>Отправить</button>
          {saved && <small role="status"> Черновик сохранён</small>}
        </form>
      )}
      {error && <p role="alert">{error}</p>}
      <CommentList category={category} id={id} />
    </DiscussionRoot>
  );
}
